// CareLogix™ ORACLE — OraclePredictiveSummary (Phase 33)
// Compact embed widget shown on the existing Oracle home dashboard.
// Links into /oracle/predictive for full command surface.
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { PALETTE, scoreColor } from "@/components/animation/MotionShared";
import { predictiveApi } from "@/api/phase33Client";

const SAMPLES = ["AAPL", "MSFT", "NVDA", "GOOGL", "AMZN", "TSLA"];

export default function OraclePredictiveSummary({ testid = "oracle-predictive-summary" }) {
  const [cards, setCards] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    let cancelled = false;
    predictiveApi.cards(SAMPLES, "stocks")
      .then((r) => { if (!cancelled) setCards(r.data?.cards || []); })
      .catch(() => { if (!cancelled) setCards([]); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  return (
    <section style={S.shell} data-testid={testid} aria-label="Predictive intelligence preview">
      <header style={S.head}>
        <div>
          <div style={S.eyebrow}>PHASE 33 · ORACLE PREDICTIVE INTELLIGENCE</div>
          <h3 style={S.title}>Predictive Signal Preview</h3>
        </div>
        <Link to="/oracle/predictive" style={S.cta} data-testid={`${testid}-open`}>Open Predictive Command™ →</Link>
      </header>
      <div style={S.grid}>
        {loading && Array.from({ length: 6 }).map((_, i) => (
          <div key={i} style={S.skeleton} />
        ))}
        {!loading && cards.map((c) => (
          <motion.article key={c.signal_id || c.symbol} style={S.card} initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }} data-testid={`${testid}-card-${c.symbol}`}>
            <header style={S.cardHead}>
              <h4 style={S.cardSym}>{c.symbol}</h4>
              <span style={{ ...S.convPill, color: PALETTE.gold }}>{(c.conviction || "").replace("_", " ").toUpperCase()}</span>
            </header>
            <div style={S.row}>
              <span style={S.k}>Opp</span>
              <div style={S.barWrap}><div style={{ ...S.barFill, width: `${c.scores?.opportunity_score || 0}%`, background: scoreColor(c.scores?.opportunity_score || 0) }} /></div>
              <span style={{ ...S.v, color: scoreColor(c.scores?.opportunity_score || 0) }}>{c.scores?.opportunity_score || 0}</span>
            </div>
            <div style={S.row}>
              <span style={S.k}>Conf</span>
              <div style={S.barWrap}><div style={{ ...S.barFill, width: `${c.scores?.confidence_score || 0}%`, background: scoreColor(c.scores?.confidence_score || 0) }} /></div>
              <span style={{ ...S.v, color: scoreColor(c.scores?.confidence_score || 0) }}>{c.scores?.confidence_score || 0}</span>
            </div>
            <div style={S.row}>
              <span style={S.k}>Risk</span>
              <div style={S.barWrap}><div style={{ ...S.barFill, width: `${c.scores?.risk_score || 0}%`, background: scoreColor(100 - (c.scores?.risk_score || 0)) }} /></div>
              <span style={{ ...S.v, color: scoreColor(100 - (c.scores?.risk_score || 0)) }}>{c.scores?.risk_score || 0}</span>
            </div>
          </motion.article>
        ))}
      </div>
      <footer style={S.foot}>Public-data only · Research only · HITL required.</footer>
    </section>
  );
}

const S = {
  shell:   { background: "linear-gradient(180deg, rgba(8,32,22,0.55), rgba(4,6,8,0.85))", border: `1px solid ${PALETTE.goldGlow}`, borderRadius: 16, padding: 14, color: PALETTE.slate3, display: "flex", flexDirection: "column", gap: 10 },
  head:    { display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10, flexWrap: "wrap" },
  eyebrow: { color: PALETTE.gold, fontFamily: "'Space Mono', monospace", fontSize: 10, letterSpacing: "0.20em" },
  title:   { margin: 0, color: PALETTE.goldBright, fontSize: 15 },
  cta:     { color: PALETTE.gold, textDecoration: "none", fontFamily: "'Space Mono', monospace", fontSize: 11, border: `1px dashed ${PALETTE.goldGlow}`, padding: "5px 11px", borderRadius: 999 },
  grid:    { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 10 },
  skeleton:{ background: "rgba(255,255,255,0.04)", borderRadius: 12, height: 120 },
  card:    { background: "#040608", border: "1px solid rgba(255,255,255,0.06)", borderRadius: 12, padding: 10, display: "flex", flexDirection: "column", gap: 6 },
  cardHead:{ display: "flex", justifyContent: "space-between", alignItems: "center" },
  cardSym: { margin: 0, color: PALETTE.goldBright, fontSize: 14 },
  convPill:{ fontFamily: "'Space Mono', monospace", fontSize: 10, letterSpacing: "0.10em" },
  row:     { display: "grid", gridTemplateColumns: "36px 1fr 32px", alignItems: "center", gap: 6 },
  k:       { color: PALETTE.gold, fontFamily: "'Space Mono', monospace", fontSize: 10 },
  v:       { fontFamily: "'Space Mono', monospace", fontSize: 11, textAlign: "right" },
  barWrap: { height: 5, background: "rgba(255,255,255,0.06)", borderRadius: 2, overflow: "hidden" },
  barFill: { height: "100%" },
  foot:    { color: PALETTE.slate5, fontFamily: "'Space Mono', monospace", fontSize: 9.5, textAlign: "right" },
};
